// Shared TypeScript types for the project-settings domain (FORGE-027 / FORGE-031).
//
// Mirrors the orchestrator's `dev-agent-state` schema:
//   - PROJECT#<id>#META       — high-level project record
//   - PROJECT#<id>#SETTINGS   — settings JSON with optimistic-locking version
//   - PROJECT#<id>#GUARDRAILS — markdown guardrails (capped at 8 KB combined)
//   - PROJECT#<id>#REPO#<full> — per-repo membership + overrides

export type ProjectTier = 'team' | 'business' | 'enterprise';
/**
 * Canonical four-plan SKU (billing-plans-spec). `ProjectTier` is the legacy
 * three-value alias kept for back-compat. See shared/types/plans.ts for the
 * full capability matrix.
 */
export type ProjectPlan = 'starter' | 'team' | 'business' | 'enterprise';
export type ProviderId = 'anthropic' | 'bedrock' | 'openai' | 'vertex' | 'meta' | 'kimi';

/** Agent types accepted by the multi-model per-agent override block. */
export const KNOWN_AGENT_TYPES = [
  'epic-planner',
  'backend',
  'web',
  'mobile',
  'qa',
  'devops',
  'secops',
  'planner-review',
  'backend-review',
  'web-review',
  'mobile-review',
  'qa-review',
  'devops-review',
  'secops-review',
] as const;
export type KnownAgentType = (typeof KNOWN_AGENT_TYPES)[number];

// ---------------------------------------------------------------------------
// META row
// ---------------------------------------------------------------------------

export interface ProjectMetaDynamoItem {
  PK: string;
  SK: 'META';
  id: string;
  name?: string;
  owner?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  archived?: boolean;
  archived_at?: string;
  tier?: ProjectTier;
  /** Canonical plan SKU (read from the BILLING row; may be absent on META). */
  plan?: ProjectPlan;
}

export interface ProjectSummary {
  id: string;
  name: string;
  owner: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  archived: boolean;
  archivedAt: string | null;
  /** Legacy three-value alias, derived from `plan`. */
  tier: ProjectTier | null;
  /** Canonical four-plan SKU. Null only on rows with no BILLING/META plan. */
  plan: ProjectPlan | null;
  /** active | suspended (payment state from the BILLING row). */
  planStatus?: 'active' | 'suspended' | null;
  repoCount: number;
  /** Owning account id. null on pre-multi-tenancy rows that were never
   *  claimed. The web filters the list to the active account by this. */
  accountId: string | null;
}

export interface ListProjectsResponse {
  data: { projects: ProjectSummary[]; nextCursor: string | null };
}

export interface ProjectDetailResponse {
  data: ProjectSummary;
}

export const PROJECT_ID_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

export interface CreateProjectRequest {
  id: string;
  name?: string;
  /** Legacy alias — accepted for back-compat. Prefer `plan`. */
  tier?: ProjectTier;
  /** Canonical four-plan SKU. */
  plan?: ProjectPlan;
  /** Account to create the project under. Defaults server-side to the
   *  caller's first account when omitted. */
  accountId?: string;
}

export interface CreateProjectResponse {
  data: ProjectSummary;
}

// ---------------------------------------------------------------------------
// SETTINGS row + nested config blocks
// ---------------------------------------------------------------------------

export interface StackBlock {
  backend?: string;
  web?: string;
  mobile?: string;
  windows?: string;
  windows_service?: string;
}

export interface RetriesBlock {
  max_fix_attempts?: number;
  max_partial_retries?: number;
}

export interface ReviewBlock {
  enabled?: boolean;
  auto_retry_on?: 'none' | 'halt' | 'halt-and-advisory';
}

export interface LimitsBlock {
  qa_test_floor?: number;
  qa_test_ceiling?: number;
  max_concurrent_stories?: number;
  max_concurrent_epics?: number;
  max_iterations?: number;
}

export interface ObservabilityBlock {
  cloud?: 'none' | 'aws' | 'gcp' | 'azure';
  deploy_logs_enabled?: boolean;
  smoke_tests_enabled?: boolean;
  smoke_tests?: { routes?: string[] };
  post_deploy_validation_enabled?: boolean;
  agent_log_access_enabled?: boolean;
  agent_log_access?: { max_calls_per_run?: number };
}

/** Per-agent override row inside `models.agents[agentType]`. */
export interface AgentModelOverride {
  provider?: ProviderId;
  model?: string;
  bedrock_region?: string;
  bedrock_assume_role_arn?: string;
  vertex_project?: string;
  vertex_location?: string;
}

/** Single fallback-chain entry. */
export interface FallbackChainEntry extends AgentModelOverride {
  /** Required at runtime — the validator flags entries without `model`. */
  model?: string;
}

// ---------------------------------------------------------------------------
// MCP servers block (FORGE-035 / FORGE-036)
// ---------------------------------------------------------------------------

export type McpTransport = 'stdio' | 'sse' | 'http';

export interface McpServer {
  /** Logical name used as the tool-prefix in agent tool lists, e.g. "aws-docs". */
  name: string;
  transport: McpTransport;
  /** The binary to spawn (e.g. "uvx", "node", "npx"). Required for stdio; omit for sse/http. */
  command?: string;
  /** CLI arguments passed to `command`. */
  args?: string[];
  /** Environment variable overrides injected into the MCP process. */
  env?: Record<string, string>;
  /** HTTP/SSE endpoint URL — required when transport is "sse" or "http". */
  url?: string;
  /**
   * Bearer token for sse/http transports, sent as `Authorization: Bearer …`.
   * Plaintext — prefer `auth_secret_arn` for anything sensitive.
   */
  auth_token?: string;
  /**
   * Secrets Manager ARN holding the bearer token. Resolved to `auth_token` by
   * the orchestrator at launch — agents never see the ARN. Preferred over a
   * plaintext `auth_token`.
   */
  auth_secret_arn?: string;
  /**
   * FORGE-164: cross-account AWS. When set, an AWS-backed MCP server (cloudwatch,
   * bedrock-kb, …) assumes this role in the CLIENT's account instead of using
   * Forge's task role. The client's role must trust Forge's task role.
   */
  aws_assume_role_arn?: string;
  aws_region?: string;
  aws_external_id?: string;
  /**
   * FORGE-169: file-based credential. The agent materializes this content to a
   * temp file at launch and points the server's KUBECONFIG env var at it — for
   * servers (kubernetes) that read a kubeconfig FILE PATH, not an env value.
   */
  kubeconfig?: string;
}

export interface ModelsBlock {
  default?: AgentModelOverride;
  agents?: Partial<Record<string, AgentModelOverride>>;
  fallback?: {
    enabled?: boolean;
    chain?: FallbackChainEntry[];
    rate_limit_window_seconds?: number;
  };
  /** FORGE-162: advisor tool (Anthropic beta, Enterprise). */
  advisor?: {
    enabled?: boolean;
    /** Advisor model id (Opus 4.7/4.8). */
    model?: string;
    /** Per-request cap on advisor calls. */
    max_uses?: number;
    /** Executor agent types the advisor attaches to. */
    agents?: string[];
  };
}

export interface ProjectSettings {
  stack?: StackBlock;
  retries?: RetriesBlock;
  review?: ReviewBlock;
  limits?: LimitsBlock;
  auto_approve?: boolean;
  observability?: ObservabilityBlock;
  /** Legacy alias — derived from `plan`; not settable from forge.yaml. */
  tier?: ProjectTier;
  /** Canonical plan SKU — authoritative on the BILLING row, surfaced read-only. */
  plan?: ProjectPlan;
  models?: ModelsBlock;
  /** Forge multi-repo layer — "main" repo for the project, org/repo slug. */
  primary_repo?: string;
  /** Optional additional repos that belong to the same project. */
  secondary_repos?: string[];
  /**
   * Forge.yaml-level blocks that also accept project-layer overrides.
   * Loosely typed here — the editor passes them through verbatim, and
   * shared/types/forgeConfig.ts carries the strict shapes for the
   * forge.yaml editor that owns them.
   */
  workflow?: Record<string, unknown>;
  branches?: Record<string, unknown>;
  byok?: Record<string, unknown>;
  mcp_servers?: McpServer[];
}

export interface SettingsDynamoItem {
  PK: string;
  SK: 'SETTINGS';
  settings: ProjectSettings;
  version: number;
  updated_at: string;
  updated_by?: string;
}

export interface ProjectSettingsView {
  settings: ProjectSettings;
  version: number;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface GetSettingsResponse {
  data: ProjectSettingsView;
}

export interface UpdateSettingsRequest {
  /** New full settings object (server applies whole-replace, not deep-merge). */
  settings: ProjectSettings;
  /**
   * Expected current version for optimistic concurrency. Use `null` when no
   * SETTINGS row exists yet (first write); use the version returned by the
   * previous GET otherwise.
   */
  expectedVersion: number | null;
}

export interface UpdateSettingsResponse {
  data: ProjectSettingsView;
}

/** Returned on 409 conflict — caller's expectedVersion mismatched the stored version. */
export interface UpdateSettingsConflictBody {
  error: {
    code: 'VERSION_CONFLICT';
    message: string;
    /** The server's current view, so the client can render a 3-way diff. */
    current: ProjectSettingsView;
  };
}

// ---------------------------------------------------------------------------
// Provider catalog (FR-11)
// ---------------------------------------------------------------------------

export interface CatalogModel {
  id: string;
  context_window?: number;
  rates: {
    input: number;
    output: number;
    cacheRead?: number;
    cacheCreate?: number;
    reasoning?: number;
  };
}

export interface CatalogProvider {
  models: CatalogModel[];
  required_fields: string[];
  optional_fields?: string[];
}

export interface ProviderCatalog {
  providers: Record<ProviderId, CatalogProvider>;
  /** Legacy three-tier provider allowances (kept for back-compat). */
  tier_allowances: Record<ProjectTier, ProviderId[]>;
  /** Canonical four-plan provider allowances (billing-plans-spec). */
  plan_allowances: Record<ProjectPlan, ProviderId[]>;
}

export interface CatalogResponse {
  data: ProviderCatalog;
}

// ---------------------------------------------------------------------------
// Repo membership rows (FR-4)
// ---------------------------------------------------------------------------

export interface RepoMembershipDynamoItem {
  PK: string;
  SK: string;             // 'REPO#<org>/<repo>'
  gsi1pk?: string;        // 'REPO#<org>/<repo>' — for repo→project lookup
  gsi1sk?: string;        // 'PROJECT#<id>'
  added_at: string;
  added_by?: string;
  updated_at?: string;
  updated_by?: string;
  overrides?: ProjectSettings;
  guardrails?: string;
}

export interface RepoMembershipSummary {
  org: string;
  repo: string;
  fullName: string;       // org/repo
  addedAt: string;
  addedBy: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  hasOverrides: boolean;
  hasGuardrails: boolean;
}

export interface ListReposResponse {
  data: { repos: RepoMembershipSummary[] };
}

/** org/repo format — must match what GitHub considers a valid repo slug. */
export const REPO_FULL_NAME_PATTERN =
  /^[a-zA-Z0-9](?:[a-zA-Z0-9._-]{0,38})\/[a-zA-Z0-9._-]{1,100}$/;

export interface AttachRepoRequest {
  /** "org/repo" */
  fullName: string;
  /** Optional initial overrides (validated server-side as a settings object). */
  overrides?: ProjectSettings;
  /** Optional initial repo-specific guardrails markdown. */
  guardrails?: string;
}

export interface AttachRepoResponse {
  data: RepoMembershipSummary;
}

export interface UpdateRepoOverridesRequest {
  overrides: ProjectSettings;
}

export interface RepoOverridesView {
  fullName: string;
  overrides: ProjectSettings;
  guardrails: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface GetRepoOverridesResponse {
  data: RepoOverridesView;
}

// ---------------------------------------------------------------------------
// Effective config (FR-5)
// ---------------------------------------------------------------------------

export type EffectiveSourceLayer = 'defaults' | 'project' | 'override' | 'yaml';

export interface EffectiveConfigEntry {
  keyPath: string;        // dotted path, e.g. "models.default.provider"
  value: unknown;
  source: EffectiveSourceLayer;
}

export interface EffectiveConfigResponse {
  data: {
    fullName: string;
    entries: EffectiveConfigEntry[];
    resolved: ProjectSettings;
  };
}

// ---------------------------------------------------------------------------
// GUARDRAILS rows (FR-6)
// ---------------------------------------------------------------------------

export const GUARDRAILS_CAP_BYTES = 8 * 1024;

export interface GuardrailsDynamoItem {
  PK: string;
  SK: 'GUARDRAILS';
  guardrails: string;
  updated_at?: string;
  updated_by?: string;
}

export interface GuardrailsView {
  text: string;
  updatedAt: string | null;
  updatedBy: string | null;
  /** Optional per-repo overlay when fetched alongside a repo. */
  repoOverlay?: {
    fullName: string;
    text: string;
    updatedAt: string | null;
    updatedBy: string | null;
  } | null;
  /** Combined byte count against the 8 KB cap. */
  combinedByteCount: number;
  combinedTruncated: boolean;
}

export interface GetGuardrailsResponse {
  data: GuardrailsView;
}

export interface UpdateGuardrailsRequest {
  guardrails: string;
}

export interface UpdateGuardrailsResponse {
  data: GuardrailsView;
}

// ---------------------------------------------------------------------------
// Audit log (FR-7)
// ---------------------------------------------------------------------------

export type AuditChangeType =
  | 'create-project'
  | 'update-settings'
  | 'attach-repo'
  | 'detach-repo'
  | 'update-overrides'
  | 'update-guardrails'
  | 'update-repo-guardrails'
  | 'archive'
  | 'refresh-config'
  | 'update-plan'
  | 'create-schedule'
  | 'update-schedule'
  | 'delete-schedule';

export interface AuditDynamoItem {
  PK: string;             // 'PROJECT#<id>'
  SK: string;             // 'AUDIT#projset#<iso-ts>#<actor>'
  actor: string;
  timestamp: string;
  change_type: AuditChangeType;
  target: string;         // 'SETTINGS' | 'REPO#<full>' | 'GUARDRAILS' | 'META'
  before?: unknown;
  after?: unknown;
  previous_version?: number | null;
  new_version?: number | null;
  notes?: string;
}

export interface AuditEntry {
  id: string;             // the SK
  timestamp: string;
  actor: string;
  changeType: AuditChangeType;
  target: string;
  before: unknown;
  after: unknown;
  previousVersion: number | null;
  newVersion: number | null;
  notes: string | null;
}

export interface ListAuditResponse {
  data: {
    entries: AuditEntry[];
    nextCursor: string | null;
  };
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// BYOK secret provisioning
// ---------------------------------------------------------------------------

export type ByokProvider = 'anthropic' | 'openai';

export interface ProvisionByokRequest {
  provider: ByokProvider;
  /** Raw API key value. Never echoed back. */
  secret: string;
  /** Optional optimistic-concurrency hint. Same shape as settings PUT. */
  expectedVersion?: number | null;
}

export interface ProvisionByokResponse {
  data: {
    provider: ByokProvider;
    /** Secret name in Secrets Manager — deterministic per project+provider. */
    secretName: string;
    /** Resulting ARN, also stored on the SETTINGS row. */
    arn: string;
    /** Updated settings view so the caller can refresh state in one call. */
    settings: ProjectSettingsView;
  };
}

// ---------------------------------------------------------------------------
// Full BYOK provider set (byok-portal-integration spec)
// Extends the legacy ByokProvider with bedrock + vertex.
// ---------------------------------------------------------------------------

/** All BYOK-capable LLM providers as surfaced in the dedicated BYOK tab. */
export type ByokFullProvider = 'anthropic' | 'openai' | 'bedrock' | 'vertex' | 'meta' | 'kimi';

/** Credential shapes per provider — sent to PUT /api/v1/projects/:id/byok */
export interface ByokCredentials {
  anthropic: { api_key: string };
  openai: { api_key: string };
  bedrock: { access_key_id: string; secret_access_key: string; region: string };
  vertex: { service_account_json: string };
  // base_url is Meta's OpenAI-compatible endpoint — an optional PLAIN
  // (non-secret) config value persisted on settings.byok.meta_base_url, not in
  // Secrets Manager. Omit to use the global META_BASE_URL env default.
  meta: { api_key: string; base_url?: string };
  // Kimi (Moonshot) is OpenAI-compatible; base_url is an optional PLAIN
  // (non-secret) override persisted on settings.byok.kimi_base_url — omit to use
  // the provider default https://api.moonshot.ai/v1.
  kimi: { api_key: string; base_url?: string };
}

/** Discriminated union over all provider+credential combos. */
export type SaveByokRequest = {
  [K in ByokFullProvider]: { provider: K; credentials: ByokCredentials[K] };
}[ByokFullProvider];

/** 200 response body from PUT /api/v1/projects/:id/byok */
export interface SaveByokResponse {
  provider: ByokFullProvider;
  secret_arn: string;
}

/**
 * The settings.byok keys that hold the provisioned ARN for each full provider.
 * Read these from settings.byok to determine whether a provider is configured.
 */
export const BYOK_ARN_KEY: Record<ByokFullProvider, string> = {
  anthropic: 'anthropic_api_key_arn',
  openai: 'openai_api_key_arn',
  bedrock: 'bedrock_credentials_arn',
  vertex: 'vertex_service_account_arn',
  meta: 'meta_api_key_arn',
  kimi: 'kimi_api_key_arn',
};

export interface ProjectErrorResponse {
  error: {
    code:
      | 'INVALID_REQUEST'
      | 'PROJECT_EXISTS'
      | 'PROJECT_NOT_FOUND'
      | 'INTERNAL_ERROR'
      | 'UNAUTHORIZED'
      | 'VERSION_CONFLICT'
      | 'REPO_EXISTS'
      | 'REPO_NOT_FOUND'
      | 'REPO_IN_USE';
    message: string;
    /** Set on REPO_IN_USE — list of in-flight epic IDs blocking detach. */
    blockingEpics?: number[];
    /** Set on PLAN_VIOLATION — details of each exceeded limit. */
    violations?: PlanViolation[];
  };
}

// ---------------------------------------------------------------------------
// Plan switching (billing-plans-spec)
// ---------------------------------------------------------------------------

/**
 * A single limit that would be exceeded if the project switches to a lower plan.
 */
export interface PlanViolation {
  /** Limit field name, e.g. 'maxConnectedRepos'. */
  field: string;
  /** Human-readable label, e.g. 'Connected repos'. */
  label: string;
  /** Current value in use. */
  currentValue: number;
  /** The limit the target plan enforces. */
  newLimit: number;
  /** Set when the violation originates from a specific project (account-level checks). */
  projectId?: string;
  projectName?: string;
}

export interface SwitchPlanRequest {
  newPlan: ProjectPlan;
}

export interface SwitchPlanResponse {
  data: ProjectSummary;
}

/** Returned as 422 when downgrading would exceed one or more plan limits. */
export interface SwitchPlanConflictBody {
  error: {
    code: 'PLAN_VIOLATION';
    message: string;
    violations: PlanViolation[];
  };
}
